/**
 * Policy Agent for Ameen Insurance Assistant
 * 
 * Handles the complete motor insurance policy creation flow.
 * THIS IS THE SINGLE SOURCE OF TRUTH FOR ALL POLICY TOOL HANDLING.
 * 
 * TOOLS (EXACT names from backend):
 * - calculate_premium: Calculate insurance premium
 * - go_to_document_agent: Route to document upload agent (then document agent routes to vehicle inspection)
 * - go_to_claim_agent: Switch to claim flow
 * - search_faq: Search FAQ
 * - go_to_router_agent: Return to menu
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager, FlowStep } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { calculatePremium } from '../../services/policyService';
import { handleSearchFAQ } from './RouterAgent';

/**
 * Create Policy Agent node
 */
export function createPolicyAgent(voiceGender: VoiceGender): AgentNode {
  return {
    id: 'policy_agent',
    name: 'Policy Agent',
    description: 'Handles complete motor insurance policy creation flow',

    entryGuardrails: noEntryRequirements(),

    exitGuardrails: {
      transitions: new Map([
        ['go_to_claim', {
          target: { agentId: 'claim_agent' },
          guardrails: []
        }],
        ['go_to_document', {
          target: { agentId: 'document_agent' },
          guardrails: []
        }],
        ['go_to_router', {
          target: { agentId: 'router_agent' },
          guardrails: []
        }]
      ]),
      fallback: { agentId: 'router_agent' }
    },

    initialPrompt: 'ابدأ الوثيقة.',

    /**
     * Handle tool calls - SINGLE SOURCE OF TRUTH for policy agent tools
     */
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[PolicyAgent] Tool called:', toolName, args);

      switch (toolName) {
        // ═══ ROUTING TOOLS ═══
        case 'go_to_router_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to menu' },
            triggersTransition: true,
            transitionTo: 'router_agent',
            transitionData: { return_to_menu: true },
          };

        case 'go_to_claim_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Switching to claim flow' },
            triggersTransition: true,
            transitionTo: 'claim_agent'
          };

        case 'go_to_document_agent':
          logger.info('[PolicyAgent] Transitioning to document_agent');
          return {
            toolName,
            args,
            result: { success: true, message: 'Starting document upload' },
            triggersTransition: true,
            transitionTo: 'document_agent',
            transitionData: {
              flow_state: args.flow_state || {}
            }
          };

        // ═══ DATA TOOLS ═══
        case 'calculate_premium':
          return await handleCalculatePremium(args, toolName, uiState);

        // ═══ FAQ TOOL ═══
        case 'search_faq':
          return await handleSearchFAQ(args, toolName);

        default:
          logger.warn('[PolicyAgent] Unknown tool:', toolName);
          return {
            toolName,
            args,
            result: { success: false, error: `Unknown tool: ${toolName}` }
          };
      }
    },

    onActivate: async (context: AgentContext) => {
      logger.info('[PolicyAgent] Activated');
    },

    onDeactivate: async (context: AgentContext) => {
      logger.info('[PolicyAgent] Deactivated');
    }
  };
}

/**
 * Handle calculate_premium tool
 */
async function handleCalculatePremium(
  args: Record<string, any>,
  toolName: string,
  uiState?: UIStateManager
): Promise<ToolCallResult> {
  try {
    // Validate required arguments
    if (args.manufactorYear === undefined || args.vehiclevalue === undefined || args.isElectricCar === undefined) {
      logger.error('[PolicyAgent] Missing required arguments for calculate_premium:', args);
      return {
        toolName,
        args,
        result: {
          success: false,
          error: 'Missing required arguments: manufactorYear, vehiclevalue, and isElectricCar are required'
        }
      };
    }

    logger.info('[PolicyAgent] Calling calculatePremium with:', args);
    uiState?.showLoading?.('جاري حساب العرض...', 'api');

    const result = await calculatePremium({
      manufactorYear: args.manufactorYear,
      vehiclevalue: args.vehiclevalue,
      isElectricCar: args.isElectricCar
    });

    uiState?.hideLoading?.();
    logger.info('[PolicyAgent] Premium calculated, companies:', result.length);

    // Update UI state
    if (uiState) {
      uiState.setCompanies(result);
      uiState.setFlowStep('package_selection');
    }

    // Build announcement: Ameen must say "ديه اسعار مبدأية" (approximate prices) before listing packages
    let packagesAnnouncement = "Premium calculated successfully. The packages are now on the user's screen. First say to the user: 'ديه اسعار مبدأية '. Then say the following packages:\n\n";
    result.forEach((company) => {
      company.companyPackages.forEach((pkg: any) => {
        packagesAnnouncement += `شركة ${company.companyNameAr}: ${pkg.packageTypeAr} بسعر ${pkg.totalpremium} جنيه.\n`;
      });
    });
    packagesAnnouncement += "\nSay these packages to the user once in Egyptian Arabic. Then wait silently for the user to select a package from the screen.\n";
    packagesAnnouncement += "[When saying GIG/GiG, spell it جي آي جي (G-I-G) letter by letter, not 'gig'].\nDo NOT call any other tool. Wait for the user to select from the screen.\n";

    return {
      toolName,
      args,
      result: {
        success: true,
        message: packagesAnnouncement,
        data: result
      }
    };
  } catch (error: any) {
    uiState?.hideLoading?.();
    logger.error('[PolicyAgent] Error calculating premium:', error);
    return {
      toolName,
      args,
      result: {
        success: false,
        error: error.message || 'Backend error while calculating premium'
      }
    };
  }
}

